/**
 * "When to check" mode picker of the AlertRuleDialog (S12) — one explanatory
 * sentence per mode. The background option is DISABLED (with the reason in
 * its tooltip — UI Quality Contract) once the dashboard's other
 * background-alert stat tiles already fill the scheduler's budget
 * (`canEnableBackgroundAlert`, maxTiles 5): a 6th would silently never run
 * while hidden, so it's rejected up front.
 */
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { BackgroundAlertBudgetResult } from '../services/alertEvaluator';

type Props = {
  background: boolean;
  budget: BackgroundAlertBudgetResult;
  onChange: (background: boolean) => void;
};

export function AlertModeRadioGroup({ background, budget, onChange }: Props) {
  const blocked = !budget.allowed && !background;
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">When to check</Label>
      <RadioGroup
        value={background ? 'background' : 'viewing'}
        onValueChange={(v) => onChange(v === 'background')}
        className="flex flex-col gap-2"
      >
        <div className="flex items-start gap-2">
          <RadioGroupItem value="viewing" id="alert-rule-mode-viewing" data-testid="alert-rule-mode-viewing" className="mt-0.5" />
          <Label htmlFor="alert-rule-mode-viewing" className="cursor-pointer text-xs font-normal">
            <span className="font-medium text-foreground">Only while viewing</span> — evaluated on
            every refresh while this dashboard is open.
          </Label>
        </div>
        <div className="flex items-start gap-2" title={blocked && !budget.allowed ? budget.reason : undefined}>
          <RadioGroupItem
            value="background"
            id="alert-rule-mode-background"
            data-testid="alert-rule-mode-background"
            disabled={blocked}
            className="mt-0.5"
          />
          <Label
            htmlFor="alert-rule-mode-background"
            className={`text-xs font-normal ${blocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          >
            <span className="font-medium text-foreground">Always, up to every 60s</span> — keeps
            checking even while this dashboard isn&apos;t the active tab.
          </Label>
        </div>
        {blocked && !budget.allowed && (
          <p className="pl-6 text-[11px] text-warning" data-testid="alert-rule-background-budget-note">
            {budget.reason}
          </p>
        )}
      </RadioGroup>
    </div>
  );
}
